import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TSP } from '../../models';

export interface CityQuery {
  page?: number;
  size?: number;
  countryId?: number;
  regionId?: number;
  search?: string;
}

export interface AdminCityQuery extends CityQuery {
  showEmptyCities?: boolean;
  showDuplicates?: boolean;
  showWithoutRegion?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class CityService {
  private readonly http = inject(HttpClient);

  public getCitiesDictionary(query: CityQuery = {}): Observable<TSP.Internal.Dictionary.City.Details[]> {
    const params = new HttpParams({
      fromObject: Object.fromEntries(Object.entries(query).filter(([_, value]) => value !== undefined)),
    });

    return this.http.get<TSP.Internal.Dictionary.City.Details[]>('/dictionaries/cities', { params });
  }

  public getCitiesList(
    query: AdminCityQuery = {},
  ): Observable<TSP.API.Helpers.EntitiesList<TSP.Internal.Dictionary.City.Details>> {
    const params = new HttpParams({
      fromObject: Object.fromEntries(Object.entries(query).filter(([_, value]) => value !== undefined)),
    });

    return this.http.get<TSP.API.Helpers.EntitiesList<TSP.Internal.Dictionary.City.Details>>('/admin/city', { params });
  }

  public getCity(id: number): Observable<TSP.Internal.Dictionary.City.Details> {
    return this.http.get<TSP.Internal.Dictionary.City.Details>(`/admin/city/${id}`);
  }

  public createCity(
    payload: TSP.Internal.Dictionary.City.CreatePayload,
  ): Observable<TSP.Internal.Dictionary.City.Details> {
    return this.http.post<TSP.Internal.Dictionary.City.Details>('/admin/city', payload);
  }

  public updateCity(
    payload: TSP.Internal.Dictionary.City.UpdatePayload,
  ): Observable<TSP.Internal.Dictionary.City.Details> {
    return this.http.put<TSP.Internal.Dictionary.City.Details>(`/admin/city/${payload.id}`, payload);
  }

  public deleteCity(id: number): Observable<TSP.API.Helpers.BooleanResponse> {
    return this.http.delete<TSP.API.Helpers.BooleanResponse>(`/admin/city/${id}`);
  }

  public replaceCity(payload: TSP.Internal.Dictionary.City.ReplacePayload): Observable<{ updatedCount: number }> {
    return this.http.post<{ updatedCount: number }>('/admin/city/replace', payload);
  }

  public fillFromGooglePlaces(cityId: number): Observable<TSP.Internal.Dictionary.City.Details> {
    return this.http.post<TSP.Internal.Dictionary.City.Details>('/admin/city/fill-data-from-google-places', {
      cityId,
    });
  }

  /** Re-reads robota.ua's city dictionary; `result` is how many cities it holds. */
  public syncRobotaUaCities(): Observable<TSP.Helpers.NumericResponse> {
    return this.http.post<TSP.Helpers.NumericResponse>('/admin/city/external-refs/robota-ua/sync', {});
  }

  public getCountries(): Observable<TSP.Internal.Dictionary.Country.Item[]> {
    return this.http.get<TSP.Internal.Dictionary.Country.Item[]>('/dictionaries/countries');
  }

  public getRegions(): Observable<TSP.Internal.Dictionary.Region.Item[]> {
    return this.http.get<TSP.Internal.Dictionary.Region.Item[]>('/dictionaries/regions');
  }
}
